import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, Clock, Users, Sparkles, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeSlots = [
  "2:00 PM",
  "4:00 PM",
  "6:00 PM",
  "8:00 PM",
];

const BookingModal = ({ isOpen, onClose, initialPackage = "standard" }) => {
  const [step, setStep] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({ booked_slots: [] });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    event_time: "",
    package_type: "patch_class",
    guest_count: 1,
    is_private_party: false,
    special_requests: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        package_type: initialPackage === "private" ? "embellishment_class" : "patch_class",
        is_private_party: initialPackage === "private",
      }));
      fetchAvailability();
    }
  }, [isOpen, initialPackage]);

  const fetchAvailability = async () => {
    try {
      const month = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}`;
      const response = await axios.get(`${API}/availability?month=${month}`);
      setAvailability(response.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) : value,
    }));
  };

  const handleDateSelect = (date) => {
    setFormData((prev) => ({ ...prev, event_date: date, event_time: "" }));
  };

  const handleTimeSelect = (time) => {
    setFormData((prev) => ({ ...prev, event_time: time }));
  };

  const isDateBooked = (dateStr, time) => {
    return availability.booked_slots?.some(
      (slot) => slot.date === dateStr && slot.time === time && (slot.is_private || slot.booked_spots >= 15)
    );
  };

  const getAvailableSpots = (dateStr, time) => {
    const slot = availability.booked_slots?.find(
      (s) => s.date === dateStr && s.time === time
    );
    if (!slot) return 15;
    if (slot.is_private) return 0;
    return 15 - slot.booked_spots;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success("Booking request submitted! We'll confirm your reservation soon.");
      onClose();
      setStep(1);
      setFormData({
        name: "",
        email: "",
        phone: "",
        event_date: "",
        event_time: "",
        package_type: "standard",
        guest_count: 1,
        is_private_party: false,
        special_requests: "",
      });
    } catch (error) {
      console.error("Error submitting booking:", error);
      const message = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const formatDateStr = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const isDatePast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        data-testid="booking-modal"
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto glass-card"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-rose" />
              <h2 className="font-heading text-2xl">
                {formData.is_private_party ? "Book Private Party" : "Book Girls Night"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="booking-close-btn"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-4 mb-8">
              {[1, 2, 3].map((s) => (
                <div 
                  key={s}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s ? "bg-rose text-black" : "bg-zinc-800 text-zinc-500"
                  }`}
                >
                  {s}
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Select Date & Time */}
              {step === 1 && (
                <div className="space-y-6" data-testid="booking-step-1">
                  <h3 className="font-heading text-xl mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-rose" />
                    Select Date & Time
                  </h3>

                  {/* Calendar */}
                  <div className="bg-zinc-900/50 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        type="button"
                        onClick={prevMonth}
                        className="p-2 text-zinc-400 hover:text-white"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span className="font-medium">
                        {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                      </span>
                      <button
                        type="button"
                        onClick={nextMonth}
                        className="p-2 text-zinc-400 hover:text-white"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                        <div key={day} className="text-xs text-zinc-500 py-2">{day}</div>
                      ))}
                      {generateCalendarDays().map((date, index) => (
                        <div key={index}>
                          {date ? (
                            <button
                              type="button"
                              onClick={() => !isDatePast(date) && handleDateSelect(formatDateStr(date))}
                              disabled={isDatePast(date)}
                              className={`w-full py-2 text-sm transition-colors ${
                                isDatePast(date)
                                  ? "text-zinc-700 cursor-not-allowed"
                                  : formData.event_date === formatDateStr(date)
                                  ? "bg-rose text-black"
                                  : "hover:bg-zinc-800 text-zinc-300"
                              }`}
                              data-testid={`date-${formatDateStr(date)}`}
                            >
                              {date.getDate()}
                            </button>
                          ) : (
                            <div className="py-2" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Time Slots */}
                  {formData.event_date && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-rose" />
                        Available Times
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {timeSlots.map((time) => {
                          const available = getAvailableSpots(formData.event_date, time);
                          const isBooked = available === 0;
                          
                          return (
                            <button
                              key={time}
                              type="button"
                              onClick={() => !isBooked && handleTimeSelect(time)}
                              disabled={isBooked}
                              className={`py-3 px-4 text-sm transition-all ${
                                isBooked
                                  ? "bg-zinc-800/50 text-zinc-600 cursor-not-allowed"
                                  : formData.event_time === time
                                  ? "bg-rose text-black"
                                  : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
                              }`}
                              data-testid={`time-${time.replace(/\s/g, "-")}`}
                            >
                              {time}
                              {!isBooked && available < 15 && (
                                <span className="block text-xs mt-1 opacity-70">
                                  {available} spots left
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={!formData.event_date || !formData.event_time}
                    className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="booking-next-step-1"
                  >
                    Continue
                  </button>
                </div>
              )}

              {/* Step 2: Package & Guests */}
              {step === 2 && (
                <div className="space-y-6" data-testid="booking-step-2">
                  <h3 className="font-heading text-xl mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-rose" />
                    Package & Guests
                  </h3>

                  {/* Package Selection */}
                  {!formData.is_private_party && (
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, package_type: "patch_class" }))}
                        className={`p-4 text-left transition-all ${
                          formData.package_type === "patch_class"
                            ? "rose-glass border-rose"
                            : "bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700"
                        }`}
                        data-testid="select-patch-pkg"
                      >
                        <p className="font-medium mb-1">Patch Class</p>
                        <p className="text-2xl font-bold text-rose">$125</p>
                        <p className="text-xs text-zinc-500">per person</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, package_type: "embellishment_class" }))}
                        className={`p-4 text-left transition-all ${
                          formData.package_type === "embellishment_class"
                            ? "bg-gold/10 border border-gold"
                            : "bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700"
                        }`}
                        data-testid="select-embellishment-pkg"
                      >
                        <p className="font-medium mb-1">Embellishment Class</p>
                        <p className="text-2xl font-bold text-gold">$200</p>
                        <p className="text-xs text-zinc-500">per person</p>
                      </button>
                    </div>
                  )}

                  {/* Guest Count */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Number of Guests</label>
                    <select
                      name="guest_count"
                      value={formData.guest_count}
                      onChange={handleChange}
                      className="w-full bg-zinc-900 border border-zinc-800 text-white p-3 focus:border-rose focus:outline-none"
                      data-testid="guest-count-select"
                    >
                      {[...Array(15)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1} {i === 0 ? "Guest" : "Guests"}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Special Requests */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Special Requests (Optional)</label>
                    <textarea
                      name="special_requests"
                      value={formData.special_requests}
                      onChange={handleChange}
                      placeholder="Birthday celebration, special colors, dietary restrictions..."
                      rows={3}
                      className="w-full bg-transparent border-b border-zinc-700 text-white py-3 focus:border-rose focus:outline-none resize-none"
                      data-testid="special-requests-input"
                    />
                  </div>

                  {/* Estimated Total */}
                  <div className="glass-card p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Estimated Total</span>
                      <span className="text-2xl font-bold text-rose">
                        ${formData.is_private_party 
                          ? formData.guest_count <= 5 ? "800" : "1,000"
                          : (formData.package_type === "embellishment_class" ? 200 : 125) * formData.guest_count
                        }
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="btn-secondary flex-1"
                      data-testid="booking-back-step-2"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="btn-primary flex-1"
                      data-testid="booking-next-step-2"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Contact Info */}
              {step === 3 && (
                <div className="space-y-6" data-testid="booking-step-3">
                  <h3 className="font-heading text-xl mb-4">Your Information</h3>

                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name *"
                      required
                      className="fashion-input"
                      data-testid="booking-name-input"
                    />
                  </div>

                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your Email *"
                      required
                      className="fashion-input"
                      data-testid="booking-email-input"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your Phone *"
                      required
                      className="fashion-input"
                      data-testid="booking-phone-input"
                    />
                  </div>

                  {/* Summary */}
                  <div className="glass-card p-4 space-y-2">
                    <h4 className="font-medium mb-3">Booking Summary</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Date</span>
                      <span>{formData.event_date}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Time</span>
                      <span>{formData.event_time}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Package</span>
                      <span className="capitalize">{formData.is_private_party ? "Private Party" : formData.package_type.replace("_", " ")}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Guests</span>
                      <span>{formData.guest_count}</span>
                    </div>
                    <div className="border-t border-white/10 pt-2 mt-2 flex justify-between">
                      <span className="font-medium">Total</span>
                      <span className="font-bold text-rose">
                        ${formData.is_private_party 
                          ? formData.guest_count <= 5 ? "800" : "1,000"
                          : (formData.package_type === "embellishment_class" ? 200 : 125) * formData.guest_count
                        }
                      </span>
                    </div>
                  </div>

                  {/* Payment Options */}
                  <div className="rose-glass p-4 space-y-3">
                    <h4 className="font-medium text-rose flex items-center gap-2">
                      <i className="fas fa-credit-card" />
                      Payment Options - Scan to Pay
                    </h4>
                    <div className="flex justify-center gap-6">
                      <div className="flex flex-col items-center">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_threaded-events/artifacts/m30jrqri_Screenshot%202026-03-07%20at%208.39.54%E2%80%AFPM.png" 
                          alt="CashApp QR"
                          className="w-24 h-24 object-contain rounded"
                        />
                        <p className="text-xs text-white mt-1">CashApp</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_threaded-events/artifacts/oil4vmnz_Croped%20Payment%20Zelle%20QR.png" 
                          alt="Zelle QR"
                          className="w-24 h-24 object-contain rounded"
                        />
                        <p className="text-xs text-white mt-1">Zelle</p>
                      </div>
                    </div>
                    <div className="text-xs text-zinc-400 text-center">
                      <p>CashApp: <span className="text-white">$dainaLaneeBrand</span></p>
                      <p>Zelle: <span className="text-white">dailightdg@gmail.com</span></p>
                    </div>
                    <div className="pt-2 border-t border-rose/20 text-xs text-zinc-400 text-center">
                      <i className="fas fa-phone mr-2" />
                      Can't pay online? Call <a href="tel:7085390789" className="text-rose hover:text-rose-light">708-539-0789</a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="btn-secondary flex-1"
                      data-testid="booking-back-step-3"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.name || !formData.email || !formData.phone}
                      className="btn-gold flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="booking-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Confirm Booking"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BookingModal;
